import { ChangeEvent, ReactElement } from 'react';
import { BaseTextFieldProps, InputProps, TextField } from '@mui/material';
import { Control, Controller, ControllerFieldState, Noop, Path } from 'react-hook-form';
import i18n from 'i18next';

type NumericTextFieldProps<TFieldValues> = BaseTextFieldProps &
  Omit<BaseTextFieldProps, 'value'> & {
    control?: Control<TFieldValues>;
    name: Path<TFieldValues>;
    required?: boolean;
    adornmentProps?: Partial<InputProps>;
    allowNegative?: boolean;
    decimalPlaces?: number;
    validateOnBlur?: boolean;
    requiredTextAdornment?: string;
    maxValue?: BoundaryValidation;
    minValue?: BoundaryValidation;
    allowZero?: boolean;
  };

export type BoundaryValidation = {
  value: number;
  errorText: string;
};

function generateDigits(count: number): string {
  let output = '';

  // Start from 2 to allow the example to be "1.23"
  for (let i = 2; i <= count + 1; i++) {
    output += (i % 10).toString();
  }
  return output;
}

function getHelperText(
  state: ControllerFieldState,
  required?: boolean,
  requiredTextAdornment?: string,
  decimalPlaces?: number,
  maxValue?: BoundaryValidation,
  minValue?: BoundaryValidation
): string | undefined {
  if (state.invalid) {
    if (state.error?.type === 'required') {
      return i18n.t('common.required-error-text');
    } else if (state.error?.type === 'isNegative') {
      return i18n.t('common.number-must-be-greater-than-zero');
    } else if (state.error?.type === 'isZero') {
      return i18n.t('common.number-cannot-be-zero-text');
    } else if ((state.error?.type === 'pattern' || state.error?.type === 'isNumber') && decimalPlaces) {
      return i18n.t('common.invalid-decimal-number-text', { fraction: generateDigits(decimalPlaces) });
    } else if (state.error?.type === 'pattern') {
      return i18n.t('common.invalid-whole-number-text');
    } else if (state.error?.type === 'maxValue' && maxValue) {
      return maxValue.errorText;
    } else if (state.error?.type === 'minValue' && minValue) {
      return minValue.errorText;
    } else {
      return i18n.t('common.invalid-number-text');
    }
  }

  if (required) {
    return `*${i18n.t('common.required-helper-text')} ${requiredTextAdornment ?? ''}`;
  }
}

function coerceToNumber(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, onChange: (number: number) => void, onBlur: Noop) {
  onBlur();

  const trimmed = e.target.value?.trim();
  if (trimmed) {
    const number = Number(trimmed);
    if (!Number.isNaN(number)) {
      onChange(number);
    }
  }
}

function generateRegex(allowNegative: boolean, decimalPlaces?: number): RegExp {
  // If we've specified the decimal places, then we need to allow the following values:
  //   1
  //   1.1
  //   .1
  //   1.
  const decimalDigits = `(\\.[0-9]{1,${decimalPlaces}})`;
  const numberRegex = decimalPlaces ? `([0-9]+\\.|[0-9]+${decimalDigits}?|${decimalDigits})` : '[0-9]+';
  const compiledRegex = `^${allowNegative ? '-?' : ''}${numberRegex}$`;
  return new RegExp(compiledRegex);
}

export function NumericTextField<TFieldValues>({
  control,
  name,
  required,
  adornmentProps,
  allowNegative,
  decimalPlaces,
  validateOnBlur,
  requiredTextAdornment,
  maxValue,
  minValue,
  allowZero,
  ...textFieldProps
}: NumericTextFieldProps<TFieldValues>): ReactElement {
  const maxInt32 = 2147483647;
  const minInt32 = -2147483648;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required,
        validate: {
          isNumber: (v) => !isNaN(Number(v)),
          isZero: (v) => (allowZero ? true : v !== '0' && v !== 0),
          isNegative: (v) => (allowNegative ? true : !String(v).startsWith('-')),
          pattern: (v) => (!required && !v ? true : generateRegex(!!allowNegative, decimalPlaces).test(String(v))),
          maxValue: (v) => (maxValue ? v <= maxValue.value : true),
          minValue: (v) => (minValue ? v >= minValue.value : true),
          isSafeNumber: (v) => v <= maxInt32 && v >= minInt32,
        },
      }}
      render={({ field, fieldState }) => {
        // See this rational for background information around not simply using the numeric type:
        // https://technology.blog.gov.uk/2020/02/24/why-the-gov-uk-design-system-team-changed-the-input-type-for-numbers/
        return (
          <TextField
            {...textFieldProps}
            error={fieldState.invalid}
            onBlur={(e) => coerceToNumber(e, field.onChange, field.onBlur)}
            onChange={field.onChange}
            value={field.value}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9.]*' }}
            helperText={getHelperText(fieldState, required, requiredTextAdornment, decimalPlaces, maxValue, minValue)}
            InputProps={adornmentProps}
          />
        );
      }}
    />
  );
}
